import { useHtmlId } from '@ipfabric/frontend-shared-assets/utils/react/HtmlId';
import type { FieldPath } from 'react-hook-form';
import { AutocompleteInput } from '../AutocompleteInput/AutocompleteInput';
import type {
  AutocompleteInputRecordProps,
  AutocompleteInputStringProps,
} from '../AutocompleteInput/AutocompleteInput.types';
import { FormField } from '../FormField/FormField';
import type { FormFieldBaseProps } from '../FormField/FormField.types';

export type AutocompleteFieldRecordProps<TOption extends Record<string, any>> =
  Omit<
    AutocompleteInputRecordProps<TOption>,
    | 'disabled'
    | 'error'
    | 'getOptionLabel'
    | 'isOptionEqualToValue'
    | 'onOptionSelect'
  > &
    FormFieldBaseProps & {
      // make getOptionLabel and isOptionEqualToValue optional with fallback to field defined by optionValueName
      getOptionLabel?: (option: TOption) => string;
      isOptionEqualToValue?: (option: TOption, value: string) => boolean;
      optionValueName: FieldPath<TOption>;
    };

export type AutocompleteFieldStringProps = Omit<
  AutocompleteInputStringProps,
  'disabled' | 'error'
> &
  FormFieldBaseProps;

const AutocompleteFieldRecord = <TOption extends Record<string, any>>({
  className,
  optionValueName,
  getOptionLabel = (option) => option[optionValueName],
  isOptionEqualToValue = (option, value) => option[optionValueName] === value,
  onChange,
  value = '',
  error,
  id,
  ...props
}: AutocompleteFieldRecordProps<TOption>) => {
  const autocompleteInputId = useHtmlId('autocompleteInput');
  const htmlForId = id ?? autocompleteInputId;
  return (
    <FormField
      className={className}
      error={error}
      htmlFor={htmlForId}
      {...props}
    >
      <AutocompleteInput.Record
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        onChange={onChange}
        onClear={() => onChange('')}
        onOptionSelect={(option) => onChange(option[optionValueName])}
        value={value}
        hasError={props.hasError || error !== undefined}
        id={htmlForId}
        {...props}
      />
    </FormField>
  );
};

const AutocompleteFieldString = ({
  value = '',
  className,
  onChange,
  error,
  id,
  ...props
}: AutocompleteFieldStringProps) => {
  const autocompleteInputId = useHtmlId('autocompleteInput');
  const htmlForId = id ?? autocompleteInputId;
  return (
    <FormField
      className={className}
      error={error}
      htmlFor={htmlForId}
      {...props}
    >
      <AutocompleteInput.String
        value={value}
        onChange={onChange}
        onClear={() => onChange('')}
        hasError={props.hasError || error !== undefined}
        id={htmlForId}
        {...props}
      />
    </FormField>
  );
};

export const AutocompleteField = {
  String: AutocompleteFieldString,
  Record: AutocompleteFieldRecord,
};
